import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, Loader2, Mail, Plus, Send } from 'lucide-react'
import Modal from '../components/ui/Modal'
import { api, apiErrorMessage } from '../lib/api'
import { toast } from '../lib/dialogs'
import { useAuth } from '../contexts/AuthContext'
import { PILL_CAMPAIGN } from '../components/marketing/campaign-status'
import { textoAHtml } from '../lib/marketing-body'
import { useTableSort } from '../lib/useTableSort'
import { MARKETING_CAMPAIGN_STATUSES } from '@jr2os/shared'
import type {
  MarketingCampaignDto,
  MarketingCampaignListResponse,
  MarketingTemplateDto,
  MarketingTemplateListResponse,
} from '@jr2os/shared'

/** Listado de campañas de correo. La pantalla de trabajo es la de detalle. */
export default function MarketingCampaigns() {
  const { t, i18n } = useTranslation(['marketing', 'common'])
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const canWrite = role === 'ADMIN' || role === 'CONSULTOR' || role === 'ANALISTA'

  const [items, setItems] = useState<MarketingCampaignDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creando, setCreando] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await api.get<MarketingCampaignListResponse>('/marketing/campaigns')
      setItems(r.items)
    } catch (err) {
      setError(apiErrorMessage(err, t('campaigns.errors.load')))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void reload()
  }, [reload])

  const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language) : '—')

  // La lista llega entera (sin paginar), así que se ordena aquí. Sin orden elegido, el de siempre (la más reciente primero).
  const { rows, header } = useTableSort(items, {
    name: (c) => c.name,
    // Orden del ciclo de vida de una campaña (borrador → … → cancelada), no el alfabético.
    status: (c) => MARKETING_CAMPAIGN_STATUSES.indexOf(c.status),
    progress: (c) => c.stats.enviados,
    // Lo que se pinta es «abiertos (porcentaje)»: se ordena por el porcentaje; sin envíos («—») va al final.
    opens: (c) => (c.stats.enviados > 0 ? c.stats.abiertos / c.stats.enviados : null),
    created: (c) => c.createdAt,
  })

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Send size={20} className="text-fuchsia-600 dark:text-fuchsia-300" />
            {t('campaigns.title')}
          </h1>
          <p className="text-xs t-muted max-w-2xl">{t('campaigns.subtitle')}</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setCreando(true)}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
          >
            <Plus size={14} /> {t('campaigns.newButton')}
          </button>
        )}
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              {header('name', t('campaigns.table.name'))}
              {header('status', t('campaigns.table.status'))}
              {header('progress', t('campaigns.table.progress'), { align: 'right' })}
              {header('opens', t('campaigns.table.opens'), { align: 'right' })}
              {header('created', t('campaigns.table.created'))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> {t('common:loading')}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center">
                  <Mail size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">{t('campaigns.empty')}</p>
                </td>
              </tr>
            )}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-base/40 hover:bg-hover">
                <td className="px-3 py-2">
                  <Link to={`/marketing/campaigns/${c.id}`} className="t-primary font-medium hover:underline">
                    {c.name}
                  </Link>
                  <div className="t-faint text-xs truncate max-w-md">{c.subject}</div>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`px-1.5 py-0.5 rounded border text-xs whitespace-nowrap ${PILL_CAMPAIGN[c.status]}`}
                  >
                    {t(`campaigns.status.${c.status}`)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right t-secondary tabular-nums">
                  {c.stats.enviados} / {c.stats.total}
                  {c.stats.fallidos > 0 && (
                    <span className="text-rose-600 dark:text-rose-300">
                      {' '}
                      · {t('campaigns.failedShort', { n: c.stats.fallidos })}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right t-secondary tabular-nums">
                  {c.stats.enviados > 0
                    ? `${c.stats.abiertos} (${Math.round((c.stats.abiertos / c.stats.enviados) * 100)} %)`
                    : '—'}
                </td>
                <td className="px-3 py-2 t-secondary whitespace-nowrap">
                  {fecha(c.createdAt)}
                  {c.createdByName && <div className="t-faint text-xs">{c.createdByName}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creando && (
        <NewCampaignModal onClose={() => setCreando(false)} onCreated={() => void reload()} />
      )}
    </div>
  )
}

function NewCampaignModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation(['marketing', 'common'])
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [busy, setBusy] = useState(false)
  const [plantillas, setPlantillas] = useState<MarketingTemplateDto[]>([])
  const [plantillaId, setPlantillaId] = useState('')

  // Solo las de correo: una plantilla de WhatsApp no es el cuerpo de un correo.
  useEffect(() => {
    void api
      .get<MarketingTemplateListResponse>('/marketing/templates')
      .then((r) => setPlantillas(r.items.filter((x) => x.channel === 'CORREO')))
      .catch(() => setPlantillas([]))
  }, [])

  const plantilla = plantillas.find((x) => x.id === plantillaId)

  const crear = async () => {
    setBusy(true)
    try {
      const r = await api.post<{ campaign: MarketingCampaignDto }>('/marketing/campaigns', {
        name,
        subject,
        // Nace con un cuerpo mínimo: el texto se escribe en la pantalla de la
        // campaña, que es donde se ve junto a la lista y al remitente.
        // Con plantilla, la campaña nace ya escrita; sin ella, con el cuerpo
        // vacío que se rellena en la pantalla de la campaña.
        bodyHtml: plantilla ? textoAHtml(plantilla.bodyText) : '<p></p>',
        bodyText: plantilla ? plantilla.bodyText : null,
        dailyCap: 150,
        throttleSeconds: 20,
      })
      onCreated()
      onClose()
      // A la campaña recién creada: es donde se escribe el texto y se monta la
      // lista, y dejar al usuario en el listado sería pedirle un clic de más.
      navigate(`/marketing/campaigns/${r.campaign.id}`)
    } catch (err) {
      toast.error(apiErrorMessage(err, t('campaigns.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-jr2-500/50'

  return (
    <Modal open onClose={onClose} title={t('campaigns.new.title')} maxWidth="max-w-lg">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="camp-name" className="text-sm font-medium t-primary block">
            {t('campaigns.new.nameLabel')}
          </label>
          <input id="camp-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          <p className="text-xs t-faint">{t('campaigns.new.nameHelp')}</p>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="camp-subject" className="text-sm font-medium t-primary block">
            {t('campaigns.new.subjectLabel')}
          </label>
          <input
            id="camp-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={inputCls}
          />
        </div>
        {plantillas.length > 0 && (
          <div className="space-y-1.5">
            <label htmlFor="camp-tpl" className="text-sm font-medium t-primary block">
              {t('campaigns.new.templateLabel')}
            </label>
            <select
              id="camp-tpl"
              value={plantillaId}
              onChange={(e) => {
                setPlantillaId(e.target.value)
                // El asunto de la plantilla solo se copia si todavía no se ha
                // escrito uno: lo que ya ha tecleado la persona manda.
                const p = plantillas.find((x) => x.id === e.target.value)
                if (p?.subject && !subject.trim()) setSubject(p.subject)
              }}
              className={inputCls}
            >
              <option value="">{t('campaigns.new.templateNone')}</option>
              {plantillas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm t-secondary hover:bg-hover">
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={busy || !name.trim() || !subject.trim()}
            onClick={() => void crear()}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {t('campaigns.new.submit')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
